<template>
  <form
    class="form"
    method="POST"
    action="javascript:;"
    @submit.prevent="submit"
  >
    <FormError v-if="formError.length > 0" :errorMessages="formError" />

    <FormLoading v-if="!formReady" />

    <CardBase v-else>
      <template #header>{{ $route.meta.title }}</template>
      <template #content>
        <FormNote class="mb-6"
          >Note: Sistem tidak akan dapat menambahkan data dengan nomor rekening
          yang sama.</FormNote
        >

        <div class="rounded mb-6 bg-red-500" v-if="readOnly">
          <p class="text-sm p-4 text-white">
            Note: ACCOUNT NUMBER TIDAK DAPAT DIRUBAH jika:
            <ul class="list-inside list-disc">
              <li>Sudah 3x WITHDRAW, atau</li>
              <li>Sudah pernah dirubah setelah ada transaksi</li>
            </ul>
            Jika ingin memindahkan
            bank ke member lain, gunakan fitur transfer member bank.
          </p>
        </div>

        <FormRow class="mb-4" :required="true">
          <template #label>Bank Group</template>
          <template #field>
            <multiselect
              :allow-empty="false"
              deselectLabel=""
              :options="listBankGroup"
              :loading="listBankGroupLoading"
              :class="$v.formData.bank_group.$error ? 'multiselect--error' : ''"
              track-by="id"
              label="name"
              tag-placeholder="Press enter to choose"
              placeholder="Choose bank group"
              v-model="formData.bank_group"
              @input="filterListBank"
              :disabled="!formCreate"
            >
              <template #noResult>No results found.</template>
            </multiselect>
            <div
              class="form-error"
              v-if="$v.formData.bank_group.$error && formSubmitted"
            >
              <span
                class="form-error__text"
                v-if="!$v.formData.bank_group.required"
                >This field is required.</span
              >
            </div>
          </template>
        </FormRow>

        <FormRow class="mb-4" :required="true">
          <template #label>Bank</template>
          <template #field>
            <multiselect
              :options="listBank"
              :loading="listBankLoading"
              :class="$v.formData.bank.$error ? 'multiselect--error' : ''"
              track-by="id"
              label="name"
              tag-placeholder="Press enter to choose"
              placeholder="Choose bank"
              :allow-empty="false"
              v-model="formData.bank"
            >
              <template #noOptions v-if="!formData.bank_group"
                >Please choose Bank Group</template
              >
              <template #noResult>No results found.</template>
            </multiselect>
            <div
              class="form-error"
              v-if="$v.formData.bank.$error && formSubmitted"
            >
              <span class="form-error__text" v-if="!$v.formData.bank.required"
                >This field is required.</span
              >
            </div>
          </template>
        </FormRow>

        <FormRow class="mb-4" :required="true">
          <template #label>Account holder</template>
          <template #field>
            <input
              :class="{
                'form__input--error':
                  $v.formData.holder.$error && formSubmitted,
              }"
              type="text"
              class="form__input"
              placeholder="Account holder name"
              v-model="formData.holder"
            />
            <div
              class="form-error"
              v-if="$v.formData.holder.$error && formSubmitted"
            >
              <span class="form-error__text" v-if="!$v.formData.holder.required"
                >This field is required.</span
              >
            </div>
          </template>
        </FormRow>

        <FormRow class="mb-4" :required="true">
          <template #label>Account number</template>
          <template #field>
            <input
              :class="{
                'form__input--error':
                  $v.formData.number.$error && formSubmitted,
              }"
              type="text"
              class="form__input"
              placeholder="Account number"
              v-model="formData.number"
              :disabled="readOnly"
            />
            <div
              class="form-error"
              v-if="$v.formData.number.$error && formSubmitted"
            >
              <span class="form-error__text" v-if="!$v.formData.number.required"
                >This field is required.</span
              >
              <span class="form-error__text" v-if="!$v.formData.number.numeric"
                >Only numbers are allowed.</span
              >
            </div>
          </template>
        </FormRow>

        <FormRow
          class="mb-4"
          v-if="userCan('unlock.member-bank-account') && formData.readonly"
        >
          <template #label></template>
          <template #field>
            <input
              type="text"
              class="form__input mb-2"
              v-model="formData.ticket"
              placeholder="Request Ticket Number"
            />

            <BaseButton
              type="button"
              buttonClass="secondary"
              @click="unlockReadOnly"
              >Unlock Readonly</BaseButton
            ></template
          >
        </FormRow>

        <FormDataLog :data="formData" v-if="!formCreate" />

        <FormRow
          class="mt-4 border-t pt-4"
          v-if="userCan('unlock.member-account') && formData.ticket_history"
        >
          <template #label>Ticket History</template>
          <template #field>
            <ul class="list-inside">
              <li
                class="text-sm"
                v-for="(ticket, idx) in formData.ticket_history"
                :id="ticket"
                :key="idx"
              >
                <router-link
                  class="link"
                  target="_blank"
                  :to="{
                    name: 'SupportTicketView',
                    params: { id: ticket },
                  }"
                  >{{ ticket }}</router-link
                >
              </li>
            </ul>
          </template>
        </FormRow>
      </template>

      <template #footer>
        <div
          class="bg-red-600 text-white p-6 mb-4 text-sm font-medium"
          v-if="updateMsg"
        >
          Note: {{ updateMsg }}
        </div>

        <FormRow class="mb-4" :required="true" v-if="updateMsg">
          <template #label>Reason for Update</template>
          <template #field>
            <textarea
              class="form__input"
              placeholder="Reason for Update"
              rows="5"
              v-model="formData.reason"
            ></textarea>
            <div
              class="form-error"
              v-if="$v.formData.reason.$error && formSubmitted"
            >
              <span class="form-error__text" v-if="!$v.formData.reason.required"
                >This field is required.</span
              >
            </div>
          </template>
        </FormRow>

        <FormRow class="mb-0">
          <template #field>
            <BaseButton type="submit">Submit</BaseButton>
          </template>
          <template #link>
            <div class="ml-4">
              or
              <router-link :to="{ name: 'MemberList' }" class="link ml-2"
                >Back to List Members</router-link
              >
            </div>
          </template>
        </FormRow>
      </template>
    </CardBase>
  </form>
</template>

<script>
import Multiselect from "vue-multiselect";
import { numeric, required, requiredIf } from "vuelidate/lib/validators";
import { getListBankOptionsMixin } from "@/mixins/getListBankOptionsMixin";
import { submitFormMixin } from "@/mixins/submitFormMixin";

export default {
  mixins: [getListBankOptionsMixin, getListBankOptionsMixin, submitFormMixin],
  components: {
    Multiselect,
  },
  validations: {
    formData: {
      bank: { required },
      bank_group: { required },
      holder: { required },
      number: { required, numeric },
      reason: {
        required: requiredIf(function () {
          return this.formData.force_update == 1;
        }),
      },
    },
  },
  data: () => {
    return {
      listBank: [],
      listBankLoading: false,

      formData: {
        force_update: 0,
        unlock: null,
      },

      hasTransaction: false,
      readOnly: false,
      member_bank_account: {},

      updateMsg: "",
    };
  },
  created() {
    if (this.formCreate) {
      this.getListBankGroup();
    } else {
      this.listBankGroupLoading = false;
    }
  },
  watch: {
    "$route.params.id": function (id) {
      if (id) {
        this.fetchData(id);
        this.formCreate = false;
      } else {
        this.formCreate = true;
        this.hasTransaction = false;
        this.readOnly = false;
        this.formData = {
          bank_group: null,
          bank: null,
          holder: "",
          number: "",
        };
      }
    },
  },
  computed: {
    submitUrl() {
      return this.formCreate
        ? "/" + this.$route.meta.endpoint(this.$route)
        : "/" +
            this.$route.meta.endpoint(this.$route) +
            "/" +
            this.formData.uuid;
    },
    fetchEndPoint() {
      return this.$route.meta.endpoint(this.$route);
    },
  },
  methods: {
    filterListBank(bankGroup) {
      delete this.formData.bank;
      this.getListBank(bankGroup.id);
    },
    fetchData(id) {
      this.formReady = false;

      const itemId = id ? id : this.formData.id;

      this.$http
        .get(
          process.env.VUE_APP_API_URL + "/" + this.fetchEndPoint + "/" + itemId
        )
        .then((res) => {
          if (res.status == 200) {
            const { data } = res.data;
            this.formData = { ...this.formData, ...data };

            this.formData.bank_id = data.bank.id;

            this.getListBankGroup();
            this.getListBank(this.formData.bank_group.id);
            this.formReady = true;

            // this.hasTransaction = data.transaction;
            this.readOnly = data.readonly;
          }
        })
        .catch((err) => {
          // console.log(err);
          if (err.response.status == 404) {
            this.$router.push({ name: this.$route.meta.baseRoute });
          } else if (err.response.status == 500) {
            this.formError = [];
            this.formError[0] = err.response.data.message;
          }
        })
        .finally(() => {});
    },
    unlockReadOnly() {
      if (!this.formLoading) {
        this.formSubmitted = true;
        this.$v.$touch();

        if (this.$v.$invalid) {
          return;
        }

        this.formLoading = true;
        this.$store.commit("toggleLoading");

        const url = process.env.VUE_APP_API_URL + this.submitUrl;

        this.formData.unlock = 1;

        this.$http({
          method: "patch",
          url: url,
          data: { bank_account: this.formData },
        })
          .then((res) => {
            if (res.status == 200 || res.status == 201) {
              this.$router.push(
                {
                  name: "MemberProfile",
                  params: { memberId: this.$route.params.memberId },
                },
                () => {
                  this.$swal({
                    position: "bottom-end",
                    showConfirmButton: false,
                    toast: true,
                    timer: 5000,
                    timerProgressBar: true,
                    title: res.data.message,
                    icon: "success",
                  });
                }
              );
            }
          })
          .catch((error) => {
            if (error.response) {
              this.formError = [];

              if (error.response.status == 404) {
                // this.formError[0] = '<pre>' + error.response.data.meta + '</pre>';
              } else if (error.response.status == 409) {
                this.updateMsg = error.response.data.data.member_bank_account;

                this.formSubmitted = false;
                this.formLoading = false;
                this.formData.force_update = 1;
              } else {
                const { data } = error.response.data;
                for (var err in data) {
                  this.formError.push(data[err]);
                }
              }
            }
          })
          .finally(() => {
            this.$store.commit("toggleLoading");
            this.formLoading = false;
            this.formSubmitted = false;
          });
      }
    },
    submit() {
      if (!this.formLoading) {
        this.formSubmitted = true;
        this.$v.$touch();

        if (this.$v.$invalid) {
          return;
        }

        this.formLoading = true;
        this.$store.commit("toggleLoading");

        const method = this.formCreate ? "post" : "patch";
        const url = process.env.VUE_APP_API_URL + this.submitUrl;

        this.$http({
          method: method,
          url: url,
          data: { bank_account: this.formData },
        })
          .then((res) => {
            if (res.status == 200 || res.status == 201) {
              this.$router.push(
                {
                  name: "MemberProfile",
                  params: { memberId: this.$route.params.memberId },
                },
                () => {
                  this.$swal({
                    position: "bottom-end",
                    showConfirmButton: false,
                    toast: true,
                    timer: 5000,
                    timerProgressBar: true,
                    title: res.data.message,
                    icon: "success",
                  });
                }
              );
            }
          })
          .catch((error) => {
            if (error.response) {
              this.formError = [];

              if (error.response.status == 404) {
                // this.formError[0] = '<pre>' + error.response.data.meta + '</pre>';
              } else if (error.response.status == 409) {
                this.updateMsg = error.response.data.data.member_bank_account;

                this.formSubmitted = false;
                this.formLoading = false;
                this.formData.force_update = 1;
              } else {
                const { data } = error.response.data;
                for (var err in data) {
                  this.formError.push(data[err]);
                }
              }
            }
          })
          .finally(() => {
            this.$store.commit("toggleLoading");
            this.formLoading = false;
            this.formSubmitted = false;
          });
      }
    },
  },
};
</script>
